import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import QRCode from 'react-qr-code'
import {
    AlertTriangle,
    ArrowDown,
    Check,
    ChevronDown,
    ChevronRight,
    Clock,
    Copy,
    ExternalLink,
    Loader2,
    PlusCircle,
    RefreshCw,
    ScanLine,
    ShieldCheck,
    Wallet,
} from 'lucide-react'
import {
    changellyRequest,
    SwapCurrency,
    SwapLimits,
    SwapQuote,
    SwapTransaction,
} from 'api/changelly'
import Tray from 'components/shared/Tray'
import { copyTextToClipboard } from 'lib/clipboard'
import { subscribeSwapStatus } from 'lib/cryptoSwapEvents'
import {
    ACTIVE_STATUSES,
    STAGE_LABELS,
    STATUS_LABELS,
    acceptsAmount,
    acceptsUsd,
    awaitingPayment,
    coinNetwork,
    coinTicker,
    currencyAtoms,
    formatAmount,
    formatBsv,
    formatCountdown,
    formatUsd,
    formatUsdShort,
    inputAmount,
    inputToUsd,
    latestSwap,
    maxFillAmount,
    maxFillUsd,
    minimumUsd,
    nativeUnit,
    paymentDeadline,
    qrPayload,
    quoteAmount,
    sentAmount,
    sentAmountShort,
    sortSwaps,
    stageIndex,
    trimZeros,
    usdPerCoin,
    usdToInput,
} from 'lib/walletSwapMath'
import styles from './walletSwap.module.scss'

// Fund with crypto: an "Add funds" row in the wallet opens a two-phase sheet.
// 1 · Set up: coin tiles (network spelled out), one amount with a sats/USD
// switch, a live quote that refreshes itself. 2 · Pay: exact amount, QR,
// address, a send-by countdown and a progress rail that maps Changelly's
// statuses. Tracking outlives this component: the server polls every open
// swap (ChangellySwapTracker) and pushes status changes over the event bus,
// which CryptoSwapStatusListener relays here through lib/cryptoSwapEvents.

const QUOTE_DEBOUNCE_MS = 500
const POLL_MS = 15000
const LIMITS_REFRESH_MS = 60000
const RECENTLY_DONE_MS = 60 * 60 * 1000
const COIN_TINTS: Record<string, { fg: string; bg: string }> = {
    btc: { fg: '#fb923c', bg: 'rgba(251, 146, 60, 0.18)' },
    eth: { fg: '#8da2fb', bg: 'rgba(141, 162, 251, 0.18)' },
    sol: { fg: '#2dd4bf', bg: 'rgba(45, 212, 191, 0.18)' },
    ltc: { fg: '#cbd5e1', bg: 'rgba(203, 213, 225, 0.16)' },
    bch: { fg: '#a3e635', bg: 'rgba(163, 230, 53, 0.16)' },
    zec: { fg: '#fbbf24', bg: 'rgba(251, 191, 36, 0.18)' },
    etn: { fg: '#38bdf8', bg: 'rgba(56, 189, 248, 0.18)' },
    usdcsol: { fg: '#60a5fa', bg: 'rgba(96, 165, 250, 0.18)' },
    usdtrx: { fg: '#34d399', bg: 'rgba(52, 211, 153, 0.18)' },
}

type Phase = 'setup' | 'pay'
type Unit = 'native' | 'usd'

function CoinDisc({ from, size = 28 }: { from: string; size?: number }) {
    const tint = COIN_TINTS[from] || { fg: '#d1d5db', bg: 'rgba(209, 213, 219, 0.16)' }
    const ticker = coinTicker(from)
    return (
        <span
            className={styles.disc}
            aria-hidden
            style={{
                width: size,
                height: size,
                color: tint.fg,
                background: tint.bg,
                fontSize: Math.round(size * (ticker.length > 3 ? 0.25 : 0.3)),
            }}
        >
            {ticker}
        </span>
    )
}

function shortAddress(value: string) {
    return value.length > 14 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value
}

function progressCopy(tx: SwapTransaction) {
    const network = coinNetwork(tx.from_currency, tx.currency_label)
    switch (tx.status) {
        case 'confirming':
            return {
                title: `Confirming on the ${network} network`,
                body: 'Payment received. Network confirmations usually take a few minutes.',
            }
        case 'exchanging':
            return {
                title: `Exchanging your ${coinTicker(tx.from_currency)} for BSV`,
                body: 'Changelly is swapping at the floating rate. Almost there.',
            }
        case 'sending':
            return {
                title: 'Sending BSV to your wallet',
                body: 'The BSV transaction is on its way to your Treechat wallet.',
            }
        default:
            return null
    }
}

function Rail({ status }: { status: string }) {
    const stage = stageIndex(status)
    if (stage === null) return null
    const finished = status === 'finished'
    const warn = status === 'hold'
    return (
        <div className={styles.rail} aria-hidden>
            {STAGE_LABELS.slice(1).map((_, i) => (
                <span
                    key={`seg-${i}`}
                    className={cn(styles.railSeg, (finished || i < stage) && styles.railSegDone)}
                    style={{
                        left: `calc(${10 + 20 * i}% + 11px)`,
                        width: 'calc(20% - 22px)',
                    }}
                />
            ))}
            {STAGE_LABELS.map((label, i) => {
                const done = finished || i < stage
                const active = !finished && i === stage
                return (
                    <span key={label} className={styles.railStep}>
                        <span
                            className={cn(
                                styles.railNode,
                                done && styles.railNodeDone,
                                active && styles.railNodeActive,
                                active && warn && styles.railNodeWarn,
                            )}
                        >
                            {done ? <Check size={12} strokeWidth={3} /> : null}
                            {active ? <span className={styles.railDot} /> : null}
                        </span>
                        <span
                            className={cn(
                                styles.railLabel,
                                done && styles.railLabelDone,
                                active && styles.railLabelActive,
                            )}
                        >
                            {label}
                        </span>
                    </span>
                )
            })}
        </div>
    )
}

function Banner({
    kind,
    icon,
    title,
    children,
}: {
    kind: 'warn' | 'error' | 'muted'
    icon: React.ReactNode
    title: string
    children?: React.ReactNode
}) {
    return (
        <div className={cn(styles.banner, styles[`banner_${kind}`])} role="status">
            <span className={styles.bannerIcon}>{icon}</span>
            <div>
                <div className={styles.bannerTitle}>{title}</div>
                {children && <div className={styles.bannerBody}>{children}</div>}
            </div>
        </div>
    )
}

function StatusChip({ status }: { status: string }) {
    const tone = ACTIVE_STATUSES.has(status)
        ? ['new', 'waiting', 'creating'].includes(status)
            ? 'orange'
            : status === 'hold'
            ? 'warn'
            : 'purple'
        : status === 'finished'
        ? 'green'
        : 'muted'
    const text =
        status === 'finished'
            ? 'Done'
            : ['new', 'waiting', 'creating'].includes(status)
            ? 'Awaiting payment'
            : status === 'hold'
            ? 'Needs verification'
            : status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
    return <span className={cn(styles.chip, styles[`chip_${tone}`])}>{text}</span>
}

type PickerOption = {
    key: string
    disc: React.ReactNode
    title: string
    sub?: string
    selected?: boolean
    disabled?: boolean
    divider?: boolean
    onSelect: () => void
}

// The currency control of a send/get field: the unit label is the button, the
// menu lists what it can become. Closes on outside click or Escape (owner).
function CurrencyPicker({
    label,
    disc,
    text,
    open,
    onToggle,
    onClose,
    options,
}: {
    label: string
    disc: React.ReactNode
    text: string
    open: boolean
    onToggle: () => void
    onClose: () => void
    options: PickerOption[]
}) {
    const choose = (option: PickerOption) => {
        if (option.disabled) return
        option.onSelect()
        onClose()
    }
    return (
        <div className={styles.picker} onMouseDown={event => event.stopPropagation()}>
            <button
                type="button"
                className={styles.pickerButton}
                aria-label={label}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={onToggle}
            >
                {disc}
                <span>{text}</span>
                <ChevronDown
                    size={16}
                    strokeWidth={2.5}
                    className={styles.pickerChevron}
                    aria-hidden
                />
            </button>
            {open && (
                <ul className={styles.pickerMenu} role="listbox" aria-label={label}>
                    {options.map(option => (
                        <li
                            key={option.key}
                            role="option"
                            aria-selected={!!option.selected}
                            aria-disabled={option.disabled || undefined}
                            tabIndex={option.disabled ? -1 : 0}
                            className={cn(
                                styles.pickerOption,
                                option.selected && styles.pickerOptionSelected,
                                option.disabled && styles.pickerOptionDisabled,
                                option.divider && styles.pickerOptionDivider,
                            )}
                            onClick={() => choose(option)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    choose(option)
                                }
                            }}
                        >
                            {option.disc}
                            <span className={styles.pickerOptionText}>
                                <span className={styles.pickerOptionTitle}>{option.title}</span>
                                {option.sub && (
                                    <span className={styles.pickerOptionSub}>{option.sub}</span>
                                )}
                            </span>
                            {option.selected && <Check size={14} aria-hidden />}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

type Availability = 'loading' | 'ready' | 'unavailable'

// The last answer from the currencies endpoint, kept across mounts so that reopening
// the wallet paints the row in its real state at once; the mount effect still refreshes it.
let knownAvailability: Availability | null = null
let knownCurrencies: SwapCurrency[] = []

export default function WalletSwap({
    address,
    usdPerBsv,
    openSwapId,
}: {
    address: string
    usdPerBsv?: number | null
    openSwapId?: string | null
}) {
    // The card renders straight away; it only leaves the loading state once the server
    // has said whether the exchange is configured (and hides if it is not).
    const [availability, setAvailability] = useState<Availability>(
        () => knownAvailability || 'loading',
    )
    const [currencies, setCurrencies] = useState<SwapCurrency[]>(() => knownCurrencies)
    const [open, setOpen] = useState(false)
    const [phase, setPhase] = useState<Phase>('setup')
    const [listOpen, setListOpen] = useState(false)
    const [sendMenuOpen, setSendMenuOpen] = useState(false)
    const [getMenuOpen, setGetMenuOpen] = useState(false)
    const [from, setFrom] = useState(() => knownCurrencies[0]?.ticker || '')
    const [unit, setUnit] = useState<Unit>('native')
    const [amount, setAmount] = useState('')
    const [inputError, setInputError] = useState('')
    const [limits, setLimits] = useState<SwapLimits | null>(null)
    const [limitsError, setLimitsError] = useState('')
    const [refRate, setRefRate] = useState<{
        from: string
        rate: string
        usdPerUnit?: string
        maximum?: string
        capped?: boolean
    } | null>(null)
    // Set when the provider will not swap this coin at all under the send cap.
    const [coinUnavailable, setCoinUnavailable] = useState('')
    const [quote, setQuote] = useState<SwapQuote | null>(null)
    const [quoteReceivedAt, setQuoteReceivedAt] = useState(0)
    const [quoting, setQuoting] = useState(false)
    const [accepted, setAccepted] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [expired, setExpired] = useState(false)
    const [refresh, setRefresh] = useState(0)
    const [creationAttempted, setCreationAttempted] = useState(false)
    const [current, setCurrent] = useState<SwapTransaction | null>(null)
    const [history, setHistory] = useState<SwapTransaction[]>([])
    const [copied, setCopied] = useState('')
    const [now, setNow] = useState(() => Date.now())
    const setupEdited = useRef(false)
    const requestId = useRef<string | null>(null)
    const inFlight = useRef(false)
    const deepLinked = useRef<string | null>(null)
    const currentRef = useRef<SwapTransaction | null>(null)
    currentRef.current = current
    const unitRef = useRef<Unit>('native')
    unitRef.current = unit
    const creationAttemptedRef = useRef(false)
    creationAttemptedRef.current = creationAttempted
    const trayOpenRef = useRef(false)
    trayOpenRef.current = open || listOpen
    const currencyRequestRef = useRef(0)

    const usdRate = usdPerBsv && Number.isFinite(usdPerBsv) && usdPerBsv > 0 ? usdPerBsv : null
    const activeLimits = limits?.from === from ? limits : null
    // USD per coin: the server's own figure when it sent one, else derived from the
    // reference quote's rate and the BSV price the wallet already shows.
    const serverPerUnit =
        refRate?.from === from && refRate.usdPerUnit ? Number(refRate.usdPerUnit) : null
    const perCoin =
        serverPerUnit && Number.isFinite(serverPerUnit) && serverPerUnit > 0
            ? serverPerUnit
            : usdPerCoin(
                  refRate?.from === from
                      ? refRate.rate
                      : quote?.from === from
                      ? quote.rate_bsv
                      : null,
                  usdRate,
              )
    const usdAssist = !!perCoin
    const unitLabel = unit === 'usd' ? 'USD' : nativeUnit(from)

    // Every coin shares one cap on a swap's send value. The server enforces it at
    // the quote's rate and reports the capped maximum (and whether the cap, not
    // the provider, set it) on every quote — the reference quote included — so
    // the sheet mirrors those figures rather than pricing the cap itself.
    const capUsd = activeLimits?.maximum_usd ? Number(activeLimits.maximum_usd) : null
    const servedQuote = quote?.from === from && quote.maximum ? quote : null
    const servedRef = refRate?.from === from && refRate.maximum ? refRate : null
    let effectiveMaximum = activeLimits?.maximum ?? null
    let capBinds = false
    if (activeLimits) {
        const served = servedQuote
            ? { maximum: servedQuote.maximum, capped: servedQuote.capped === true }
            : servedRef
            ? { maximum: servedRef.maximum!, capped: servedRef.capped === true }
            : null
        try {
            if (served && currencyAtoms(served.maximum) >= currencyAtoms(activeLimits.minimum)) {
                effectiveMaximum = served.maximum
                capBinds = served.capped
            }
        } catch (_) {
            // A malformed maximum leaves the provider maximum in force.
        }
    }
    const capLabel = capUsd && Number.isFinite(capUsd) && capUsd > 0 ? formatUsdShort(capUsd) : ''
    // The minimum in dollars at the reference rate, rounded up so it is always a
    // valid amount to fill or type: the Min chip, its error and the $ chips share it.
    let minUsd: number | null = null
    if (activeLimits) {
        try {
            minUsd = minimumUsd(from, activeLimits.minimum, perCoin)
        } catch (_) {
            // A malformed minimum keeps the chip in native units only.
        }
    }

    // Freeze the provider amount after creation starts so a lost-response retry
    // uses the original quote even if the USD reference price changes.
    const nativeText =
        creationAttempted && quote
            ? inputAmount(from, quote.amount)
            : unit === 'usd'
            ? usdToInput(from, Number(amount), perCoin)
            : amount
    let normalizedAmount = ''
    let amountError = inputError
    if (nativeText && !amountError) {
        try {
            normalizedAmount = quoteAmount(from, nativeText)
            const atoms = currencyAtoms(normalizedAmount)
            if (atoms <= 0n) amountError = 'Enter an amount greater than zero.'
            else if (activeLimits && atoms < currencyAtoms(activeLimits.minimum))
                amountError = `Minimum is ${formatAmount(from, activeLimits.minimum)}${
                    minUsd ? ` (≈ ${formatUsdShort(minUsd)})` : ''
                }.`
            else if (
                activeLimits &&
                effectiveMaximum &&
                (unit === 'usd' && capBinds && capUsd
                    ? Number(amount) > capUsd
                    : atoms > currencyAtoms(effectiveMaximum))
            )
                amountError = capBinds
                    ? `Maximum is about ${capLabel || 'the send limit'} per swap (≈ ${formatAmount(
                          from,
                          effectiveMaximum,
                      )}).`
                    : `Maximum is ${formatAmount(from, effectiveMaximum)}.`
        } catch (_) {
            amountError =
                from === 'btc' ? 'Enter a positive whole number of sats.' : 'Enter a valid amount.'
        }
    }

    const sortedHistory = useMemo(() => sortSwaps(history), [history])
    const featured = useMemo(() => {
        const active = sortedHistory.find(tx => ACTIVE_STATUSES.has(tx.status))
        if (active) return active
        const latest = sortedHistory[0]
        if (
            latest &&
            latest.status === 'finished' &&
            now - Date.parse(latest.completed_at || latest.created_at) < RECENTLY_DONE_MS
        )
            return latest
        return null
    }, [sortedHistory, now])

    const saveTransaction = useCallback((tx: SwapTransaction) => {
        setCurrent(value => (value?.id === tx.id ? latestSwap(value, tx) : value))
        setHistory(previous =>
            [
                latestSwap(
                    previous.find(item => item.id === tx.id),
                    tx,
                ),
                ...previous.filter(item => item.id !== tx.id),
            ].slice(0, 20),
        )
    }, [])

    const loadHistory = useCallback(async (adoptActive = false) => {
        const transactions = await changellyRequest<SwapTransaction[]>('transactions')
        setHistory(previous =>
            sortSwaps([
                ...transactions.map(tx =>
                    latestSwap(
                        previous.find(item => item.id === tx.id),
                        tx,
                    ),
                ),
                ...previous.filter(item => !transactions.some(tx => tx.id === item.id)),
            ]).slice(0, 20),
        )
        const live = currentRef.current
        if (live) {
            const fresh = transactions.find(tx => tx.id === live.id)
            if (fresh)
                setCurrent(value => (value?.id === fresh.id ? latestSwap(value, fresh) : value))
        } else if (adoptActive && !setupEdited.current) {
            const active = sortSwaps(transactions).find(tx => ACTIVE_STATUSES.has(tx.status))
            if (active) {
                setCurrent(active)
                setPhase('pay')
            }
        }
        return transactions
    }, [])

    useEffect(() => {
        let active = true
        const request = ++currencyRequestRef.current
        changellyRequest<{ enabled: boolean; currencies: SwapCurrency[] }>('currencies')
            .then(result => {
                // A sheet refresh can finish first, or this wallet may already be unmounted.
                if (!active || request !== currencyRequestRef.current) return
                knownAvailability = result.enabled ? 'ready' : 'unavailable'
                knownCurrencies = result.currencies
                // Never pull the card out from under an open tray; the next mount hides it.
                if (knownAvailability === 'unavailable' && trayOpenRef.current) return
                setAvailability(knownAvailability)
                setCurrencies(result.currencies)
                setFrom(value =>
                    result.currencies.some(c => c.ticker === value)
                        ? value
                        : result.currencies[0]?.ticker || '',
                )
            })
            .catch(() => {
                // Leave a retryable entry point when configured service availability is unknown.
                if (active && request === currencyRequestRef.current) setAvailability('ready')
            })
        return () => {
            active = false
        }
    }, [])

    // The row shows a live swap without opening anything, so history loads with the
    // wallet, alongside the currency list rather than after it.
    useEffect(() => {
        let active = true
        loadHistory().catch(() => {
            if (active) setHistory([])
        })
        return () => {
            active = false
        }
    }, [loadHistory])

    useEffect(() => {
        if (!open) return
        let active = true
        const request = ++currencyRequestRef.current
        loadHistory(!creationAttemptedRef.current).catch(err => {
            if (active) setError(err.message)
        })
        changellyRequest<{ enabled: boolean; currencies: SwapCurrency[] }>('currencies')
            .then(available => {
                if (!active || request !== currencyRequestRef.current) return
                knownAvailability = available.enabled ? 'ready' : 'unavailable'
                knownCurrencies = available.currencies
                setCurrencies(available.currencies)
                setFrom(value =>
                    available.currencies.some(c => c.ticker === value)
                        ? value
                        : available.currencies[0]?.ticker || '',
                )
            })
            .catch(err => {
                if (active && request === currencyRequestRef.current) setError(err.message)
            })
        return () => {
            active = false
        }
    }, [open, loadHistory])

    // Status pushes from the server-side tracker (relayed by CryptoSwapStatusListener).
    useEffect(() => subscribeSwapStatus(event => saveTransaction(event)), [saveTransaction])

    useEffect(() => {
        if (!open || phase !== 'setup' || !from || creationAttempted) return
        let active = true
        const controller = new AbortController()
        const load = async () => {
            try {
                const result = await changellyRequest<SwapLimits>(
                    `limits?from_currency=${encodeURIComponent(from)}`,
                    undefined,
                    controller.signal,
                )
                if (
                    result.from !== from ||
                    currencyAtoms(result.minimum) <= 0n ||
                    currencyAtoms(result.maximum) < currencyAtoms(result.minimum)
                )
                    throw new Error('Swap limits are unavailable.')
                if (active) {
                    setLimits(result)
                    setLimitsError('')
                }
            } catch (err) {
                if (active) {
                    setLimits(null)
                    setLimitsError('Could not load swap limits. Try again.')
                }
            }
        }
        setLimitsError('')
        load()
        const timer = window.setInterval(load, LIMITS_REFRESH_MS)
        return () => {
            active = false
            controller.abort()
            window.clearInterval(timer)
        }
    }, [open, phase, from, refresh, creationAttempted])

    // One reference quote at the minimum gives the USD switch, the $ chips and
    // the "≈ $" line a rate before the user has typed anything.
    useEffect(() => {
        if (!open || phase !== 'setup' || !activeLimits || !address) return
        if (coinUnavailable) return
        let active = true
        const controller = new AbortController()
        changellyRequest<SwapQuote>(
            'quote',
            { from_currency: from, amount: activeLimits.minimum, address, probe: true },
            controller.signal,
        )
            .then(result => {
                if (!active || result.from !== from) return
                setCoinUnavailable('')
                setRefRate(previous => ({
                    from,
                    rate: result.rate_bsv,
                    // A dollar amount converts at one rate until the user leaves dollar mode;
                    // moving it under them would re-quote in a loop. Maximum and cap refresh.
                    usdPerUnit:
                        unitRef.current === 'usd' && previous?.from === from && previous.usdPerUnit
                            ? previous.usdPerUnit
                            : result.usd_per_unit,
                    maximum: result.maximum,
                    capped: result.capped,
                }))
            })
            .catch((err: Error & { code?: string }) => {
                if (!active) return
                // The server says so explicitly when the provider will not swap this coin
                // under the cap at all. Every other failure stays silent — the USD assist is
                // optional and the BSV estimate never depends on it.
                if (err.code === 'coin_unavailable') setCoinUnavailable(err.message)
            })
        return () => {
            active = false
            controller.abort()
        }
    }, [open, phase, from, activeLimits, address, coinUnavailable])

    useEffect(() => {
        // An uncertain create response must keep the same quote and request ID for retry.
        if (creationAttempted) return
        let active = true
        const controller = new AbortController()
        setQuote(null)
        setAccepted(false)
        requestId.current = null
        setQuoting(false)
        if (
            !open ||
            phase !== 'setup' ||
            current ||
            !activeLimits ||
            !normalizedAmount ||
            amountError ||
            !address ||
            coinUnavailable
        )
            return
        setQuoting(true)
        setError('')
        const timer = window.setTimeout(async () => {
            try {
                const result = await changellyRequest<SwapQuote>(
                    'quote',
                    { from_currency: from, amount: normalizedAmount, address },
                    controller.signal,
                )
                if (active) {
                    setQuote(result)
                    setQuoteReceivedAt(Date.now())
                    setRefRate(previous => ({
                        from,
                        rate: result.rate_bsv,
                        usdPerUnit:
                            unitRef.current === 'usd' &&
                            previous?.from === from &&
                            previous.usdPerUnit
                                ? previous.usdPerUnit
                                : result.usd_per_unit,
                        maximum: result.maximum,
                        capped: result.capped,
                    }))
                    requestId.current = crypto.randomUUID()
                }
            } catch (err) {
                if (active) setError(err.message || 'Could not update the estimate. Try again.')
            } finally {
                if (active) setQuoting(false)
            }
        }, QUOTE_DEBOUNCE_MS)
        return () => {
            active = false
            controller.abort()
            window.clearTimeout(timer)
        }
    }, [
        open,
        phase,
        current?.id,
        from,
        normalizedAmount,
        amount,
        amountError,
        address,
        activeLimits,
        refresh,
        creationAttempted,
        coinUnavailable,
    ])

    useEffect(() => {
        setCoinUnavailable('')
    }, [from, refresh])

    useEffect(() => {
        setCopied('')
    }, [current?.id])

    useEffect(() => {
        if (!copied) return
        const timer = window.setTimeout(() => setCopied(''), 2500)
        return () => window.clearTimeout(timer)
    }, [copied])

    useEffect(() => {
        if (!open) return
        const timer = window.setInterval(() => setNow(Date.now()), 1000)
        return () => window.clearInterval(timer)
    }, [open])

    useEffect(() => {
        if (!quote) return
        const check = () => setExpired(Date.now() >= Date.parse(quote.expires_at))
        check()
        const timer = window.setInterval(check, 1000)
        return () => window.clearInterval(timer)
    }, [quote])

    // A quote that ran out while the form sat untouched refreshes itself at once.
    // One that arrived already expired (clock skew, provider hiccup) retries on a
    // slow cadence instead, so the sheet cannot spin on a bad clock.
    useEffect(() => {
        if (!quote || !expired || creationAttempted || busy) return
        const arrivedExpired = Date.parse(quote.expires_at) - quoteReceivedAt < 5000
        const timer = window.setTimeout(
            () => setRefresh(value => value + 1),
            arrivedExpired ? 15000 : 0,
        )
        return () => window.clearTimeout(timer)
    }, [quote, expired, creationAttempted, busy, quoteReceivedAt])

    useEffect(() => {
        if (!open || !current || !ACTIVE_STATUSES.has(current.status)) return
        let active = true
        let timer: number
        const poll = async () => {
            try {
                const tx = await changellyRequest<SwapTransaction>(`transactions/${current.id}`)
                if (active) {
                    saveTransaction(tx)
                    setError('')
                }
            } catch (err) {
                if (active)
                    setError(
                        'Status could not refresh. Your swap is still saved; try again shortly.',
                    )
            }
            if (active) timer = window.setTimeout(poll, POLL_MS)
        }
        poll()
        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [open, current?.id, current?.status, saveTransaction])

    useEffect(() => {
        if (!sendMenuOpen && !getMenuOpen) return
        const close = () => {
            setSendMenuOpen(false)
            setGetMenuOpen(false)
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            // Escape closes the menu, not the sheet behind it.
            event.stopPropagation()
            close()
        }
        document.addEventListener('mousedown', close)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', close)
            document.removeEventListener('keydown', onKey)
        }
    }, [sendMenuOpen, getMenuOpen])

    const clearQuote = () => {
        setQuote(null)
        setAccepted(false)
        requestId.current = null
        setError('')
    }

    const openSheet = (tx?: SwapTransaction | null) => {
        setError('')
        setupEdited.current = false
        if (tx) {
            setCurrent(tx)
            setPhase('pay')
        } else if (!current || !creationAttempted) {
            setCurrent(null)
            setPhase('setup')
        }
        setListOpen(false)
        setOpen(true)
    }

    const closeSheet = () => setOpen(false)

    const startNewSwap = () => {
        setupEdited.current = true
        setCurrent(null)
        setCreationAttempted(false)
        clearQuote()
        setAmount('')
        setInputError('')
        setPhase('setup')
    }

    // Notifications can refer to swaps older than the 20-item recent history.
    useEffect(() => {
        if (availability !== 'ready' || !openSwapId || deepLinked.current === openSwapId) return
        let active = true
        const controller = new AbortController()
        const cached = history.find(item => item.id === openSwapId)
        const show = (tx: SwapTransaction) => {
            if (!active) return
            deepLinked.current = openSwapId
            openSheet(tx)
        }
        if (cached) show(cached)
        else
            changellyRequest<SwapTransaction>(
                `transactions/${encodeURIComponent(openSwapId)}`,
                undefined,
                controller.signal,
            )
                .then(show)
                .catch(err => {
                    if (!active) return
                    deepLinked.current = openSwapId
                    setOpen(true)
                    setError(err.message || 'Could not load this swap.')
                })
        return () => {
            active = false
            controller.abort()
        }
    }, [availability, openSwapId, history])

    const selectCoin = (ticker: string) => {
        setFrom(ticker)
        setCoinUnavailable('')
        // A dollar amount means the same thing for any coin; a native amount does not.
        if (unit !== 'usd') setAmount('')
        setInputError('')
        clearQuote()
    }

    const switchUnit = (next: Unit) => {
        setupEdited.current = true
        if (next === unit) return
        if (next === 'usd' && !perCoin) return
        let converted = ''
        if (amount) {
            if (next === 'usd') {
                const usd = inputToUsd(from, amount, perCoin)
                if (usd) {
                    // Nearest cent, stepping back one only when the round trip would put a
                    // native maximum over the provider's bound.
                    let cents = Math.round(usd * 100)
                    if (effectiveMaximum && !capBinds) {
                        try {
                            const roundTrip = quoteAmountSafe(
                                from,
                                usdToInput(from, cents / 100, perCoin),
                            )
                            if (currencyAtoms(roundTrip) > currencyAtoms(effectiveMaximum))
                                cents -= 1
                        } catch (_) {
                            // Leave the nearest cent in place if the round trip cannot be checked.
                        }
                    }
                    converted = trimZeros((cents / 100).toFixed(2))
                }
            } else {
                converted = usdToInput(from, Number(amount), perCoin)
                // A dollar amount at the cap converts a few atoms over the floor-rounded
                // maximum; clamp so the switch never produces an amount the sheet refuses.
                try {
                    if (
                        effectiveMaximum &&
                        converted &&
                        currencyAtoms(quoteAmountSafe(from, converted)) >
                            currencyAtoms(effectiveMaximum)
                    )
                        converted = inputAmount(from, effectiveMaximum)
                } catch (_) {
                    // Leave the conversion as is if it cannot be checked.
                }
            }
        }
        setUnit(next)
        setAmount(converted)
        setInputError('')
    }

    const toggleUsd = () => switchUnit(unit === 'usd' ? 'native' : 'usd')

    const onAmountChange = (value: string) => {
        setupEdited.current = true
        const accepts = unit === 'usd' ? acceptsUsd(value) : acceptsAmount(from, value)
        if (!accepts) {
            setInputError(
                unit === 'usd'
                    ? 'Use numbers only, with up to 2 decimal places.'
                    : from === 'btc'
                    ? 'Use whole sats only; no decimal points, signs, or letters.'
                    : 'Use numbers only, with up to 8 decimal places.',
            )
            clearQuote()
            return
        }
        setInputError('')
        setAmount(value)
    }

    const fillNative = (value: string) => {
        setupEdited.current = true
        setUnit('native')
        setAmount(inputAmount(from, value))
        setInputError('')
    }

    // Min keeps the field's unit: the exact minimum in native units, or its
    // rounded-up dollar figure so the round trip never lands under the minimum.
    const fillMin = () => {
        if (!activeLimits) return
        if (unit === 'usd' && minUsd) {
            setupEdited.current = true
            setAmount(trimZeros(minUsd.toFixed(2)))
            setInputError('')
            return
        }
        fillNative(activeLimits.minimum)
    }

    // Max fills a little under the limit (MAX_FILL_MARGIN_PERCENT) so a price move
    // between the reference rate and the quote does not push the swap over the cap.
    const fillMax = () => {
        if (!activeLimits || !effectiveMaximum) return
        if (unit === 'usd' && perCoin) {
            // Under the cap, Max starts from the cap itself; otherwise from the
            // provider maximum in dollars.
            const usd =
                capBinds && capUsd
                    ? capUsd
                    : inputToUsd(from, inputAmount(from, effectiveMaximum), perCoin)
            if (usd) {
                setupEdited.current = true
                setAmount(
                    trimZeros(maxFillUsd(from, usd, activeLimits.minimum, perCoin).toFixed(2)),
                )
                setInputError('')
                return
            }
        }
        fillNative(maxFillAmount(effectiveMaximum, activeLimits.minimum))
    }

    const fillUsd = (usd: number) => {
        setupEdited.current = true
        if (!perCoin) return
        setUnit('usd')
        setAmount(String(usd))
        setInputError('')
    }

    const createSwap = async () => {
        if (
            !quote ||
            !requestId.current ||
            inFlight.current ||
            !quoteMatchesInput ||
            (!creationAttempted && expired) ||
            !accepted
        )
            return
        inFlight.current = true
        setCreationAttempted(true)
        setBusy(true)
        setError('')
        try {
            const tx = await changellyRequest<SwapTransaction>('transactions', {
                quote_token: quote.quote_token,
                request_id: requestId.current,
                accepted_terms: accepted,
            })
            saveTransaction(tx)
            setCurrent(tx)
            clearQuote()
            setPhase('pay')
        } catch (err) {
            // Preserve this request ID on retry: a lost response must not create another swap.
            setError(
                `${
                    err.message || 'Could not confirm swap creation.'
                } Retry or reopen the wallet to check saved swaps.`,
            )
        } finally {
            inFlight.current = false
            setBusy(false)
        }
    }

    const refreshStatus = async () => {
        if (!current) return
        setBusy(true)
        try {
            saveTransaction(await changellyRequest<SwapTransaction>(`transactions/${current.id}`))
            setError('')
        } catch (err) {
            setError(err.message)
        } finally {
            setBusy(false)
        }
    }

    const copy = async (value: string) => {
        try {
            if (!(await copyTextToClipboard(value))) throw new Error('Copy failed')
            setCopied(value)
            setError('')
        } catch (_) {
            setCopied('')
            setError('Copy failed. Select and copy the address below.')
        }
    }

    if (availability === 'unavailable') return null
    const loading = availability === 'loading'

    const quoteMatchesInput =
        !!quote &&
        quote.address === address &&
        quote.from === from &&
        !!normalizedAmount &&
        !amountError &&
        !!activeLimits &&
        currencyAtoms(quote.amount) === currencyAtoms(normalizedAmount)
    const showQuote = !!quote && quoteMatchesInput
    const quoteUsd = showQuote && usdRate ? Number(quote!.expected_bsv) * usdRate : null
    const approxUsd = unit === 'native' ? inputToUsd(from, amount, perCoin) : null
    const approxLine =
        unit === 'usd'
            ? nativeText
                ? `≈ ${formatAmount(from, quoteAmountSafe(from, nativeText))}`
                : ''
            : approxUsd
            ? `≈ ${formatUsd(approxUsd)}`
            : ''
    const ctaDisabled =
        busy ||
        !accepted ||
        (expired && !creationAttempted) ||
        !quoteMatchesInput ||
        !address ||
        (!!coinUnavailable && !creationAttempted)

    const payin = current?.payin_address || null
    const awaiting = !!current && awaitingPayment(current, now)
    const windowEnded = !!current && ['new', 'waiting'].includes(current.status) && !awaiting
    const progress = current ? progressCopy(current) : null
    const failedLike =
        !!current &&
        ['failed', 'creation_unknown', 'overdue', 'expired', 'refunded'].includes(current.status)
    const payload = current ? qrPayload(current) : null
    const network = current ? coinNetwork(current.from_currency, current.currency_label) : ''
    const receivedBsv = current ? formatBsv(current.actual_bsv || current.expected_bsv) : null

    return (
        <section className={styles.card} data-testid="wallet-swap">
            <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                    <PlusCircle size={15} aria-hidden />
                    <span>Add funds</span>
                </div>
                <span className={styles.cardNote}>exchange by Changelly</span>
            </div>
            <button
                type="button"
                className={styles.row}
                aria-label="Fund with crypto"
                aria-busy={loading || undefined}
                disabled={loading}
                onClick={() => openSheet(featured)}
            >
                <span className={styles.stack} aria-hidden>
                    <CoinDisc from="btc" size={28} />
                    <CoinDisc from="eth" size={28} />
                    <CoinDisc from="usdtrx" size={28} />
                </span>
                <span className={styles.rowText}>
                    <span className={styles.rowTitle}>Fund with crypto</span>
                    <span className={styles.rowSub}>
                        {featured ? (
                            <FeaturedStatus tx={featured} now={now} />
                        ) : (
                            'BTC, ETH, SOL, USDT and more'
                        )}
                    </span>
                </span>
                {loading ? (
                    featured ? (
                        <Loader2
                            size={18}
                            className={cn(styles.rowChevron, styles.spin)}
                            aria-hidden
                        />
                    ) : (
                        <span className={cn(styles.fundPill, styles.fundPillLoading)} aria-hidden>
                            <span className={styles.fundPillGhost}>Fund</span>
                            <Loader2 size={15} className={styles.spin} />
                        </span>
                    )
                ) : featured ? (
                    <ChevronRight size={18} className={styles.rowChevron} aria-hidden />
                ) : (
                    <span className={styles.fundPill}>Fund</span>
                )}
            </button>
            {sortedHistory.length > 0 && (
                <button
                    type="button"
                    className={styles.recentRow}
                    disabled={loading}
                    onClick={() => setListOpen(true)}
                >
                    <span>Recent swaps</span>
                    <span className={styles.recentCount}>
                        {sortedHistory.length}
                        <ChevronRight size={16} aria-hidden />
                    </span>
                </button>
            )}

            {listOpen && (
                <Tray
                    title="Recent swaps"
                    onDismiss={() => setListOpen(false)}
                    dialogTestId="swap-history"
                    bodyClassName={styles.sheetBody}
                >
                    <div className={styles.historyList}>
                        {sortedHistory.map(tx => (
                            <button
                                type="button"
                                key={tx.id}
                                className={styles.historyRow}
                                onClick={() => openSheet(tx)}
                            >
                                <CoinDisc from={tx.from_currency} size={30} />
                                <span className={styles.historyText}>
                                    <span className={styles.historyTitle}>
                                        {sentAmountShort(tx)}
                                        <span className={styles.historyArrow}> → </span>
                                        {formatBsv(tx.actual_bsv || tx.expected_bsv) || '—'} BSV
                                    </span>
                                    <span className={styles.historyWhen}>
                                        {sentAmount(tx)} ·{' '}
                                        {new Date(tx.created_at).toLocaleDateString()}
                                    </span>
                                </span>
                                <StatusChip status={tx.status} />
                            </button>
                        ))}
                    </div>
                </Tray>
            )}

            {open && (
                <Tray
                    title="Fund with crypto"
                    onDismiss={closeSheet}
                    dialogTestId="wallet-swap-sheet"
                    bodyClassName={styles.sheetBody}
                >
                    <div className={styles.phaseBar} aria-hidden>
                        <span
                            className={cn(
                                styles.phase,
                                phase === 'pay' ? styles.phaseDone : styles.phaseActive,
                            )}
                        >
                            <span className={styles.phaseTrack} />1 · Set up
                        </span>
                        <span className={cn(styles.phase, phase === 'pay' && styles.phaseActive)}>
                            <span className={styles.phaseTrack} />2 · Pay
                        </span>
                    </div>
                    {error && (
                        <p role="alert" className={styles.error}>
                            <AlertTriangle size={13} aria-hidden />
                            <span>{error}</span>
                        </p>
                    )}

                    {phase === 'pay' && current ? (
                        <>
                            <Rail status={current.status} />
                            <p role="status" className={styles.statusLine}>
                                {STATUS_LABELS[current.status] || `Status: ${current.status}`}
                            </p>

                            {awaiting && payin && (
                                <>
                                    <div className={styles.hero}>
                                        <div className={styles.eyebrow}>Send exactly</div>
                                        <div
                                            className={styles.heroAmount}
                                            data-testid="swap-send-amount"
                                        >
                                            {sentAmountShort(current)}
                                        </div>
                                        <div className={styles.heroSub}>
                                            {current.from_currency === 'btc' &&
                                                `${current.from_amount} BTC · `}
                                            {sentAmount(current)}
                                        </div>
                                        <span className={styles.badge}>{network} network only</span>
                                    </div>
                                    {payload && (
                                        <div className={styles.qrWrap}>
                                            <div className={styles.qrTile} data-testid="swap-qr">
                                                <QRCode
                                                    value={payload}
                                                    size={168}
                                                    bgColor="#ffffff"
                                                    fgColor="#0a0e18"
                                                    level="M"
                                                />
                                            </div>
                                            <div className={styles.scanLine}>
                                                <ScanLine size={13} aria-hidden />
                                                <span>
                                                    Scan with your{' '}
                                                    {coinTicker(current.from_currency)} wallet, or
                                                    copy the address
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.addressChip}>
                                        <code className={styles.addressText}>{payin}</code>
                                        <button
                                            type="button"
                                            className={cn(
                                                styles.copyBadge,
                                                copied === payin && styles.copyBadgeDone,
                                            )}
                                            aria-label={
                                                copied === payin
                                                    ? 'Copied!'
                                                    : 'Copy deposit address'
                                            }
                                            onClick={() => copy(payin)}
                                        >
                                            {copied === payin ? (
                                                <>
                                                    <Check
                                                        size={14}
                                                        strokeWidth={2.5}
                                                        aria-hidden
                                                    />
                                                    <span>copied</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={14} strokeWidth={2.5} aria-hidden />
                                                    <span>copy</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {copied === payin && (
                                        <p role="status" className={styles.srOnly}>
                                            Deposit address copied.
                                        </p>
                                    )}
                                    {current.payin_extra_id && (
                                        <div className={styles.memo}>
                                            <strong>Required memo/tag:</strong>{' '}
                                            <code>{current.payin_extra_id}</code>
                                        </div>
                                    )}
                                    <div className={styles.infoRow}>
                                        <Clock size={18} className={styles.warnIcon} aria-hidden />
                                        <div>
                                            <div className={styles.infoTitle}>
                                                Send within{' '}
                                                <span className={styles.tabular}>
                                                    {formatCountdown(
                                                        paymentDeadline(current) - now,
                                                    )}
                                                </span>
                                            </div>
                                            <div className={styles.infoSub}>
                                                One payment only. Use the {network} network and
                                                don’t reuse this address.
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {windowEnded && (
                                <Banner
                                    kind="muted"
                                    icon={<Clock size={18} />}
                                    title="Payment window ended"
                                >
                                    Don’t send funds to this address. Start a new swap for fresh
                                    instructions.
                                </Banner>
                            )}
                            {progress && (
                                <div className={styles.progressBlock}>
                                    <span className={styles.spinner} aria-hidden />
                                    <div className={styles.progressTitle}>{progress.title}</div>
                                    <div className={styles.progressSub}>{progress.body}</div>
                                </div>
                            )}
                            {current.status === 'hold' && (
                                <Banner
                                    kind="warn"
                                    icon={<ShieldCheck size={18} />}
                                    title="Held for ID verification"
                                >
                                    Contact{' '}
                                    <a href="mailto:security@changelly.com">
                                        security@changelly.com
                                    </a>{' '}
                                    with your swap ID to complete verification.
                                </Banner>
                            )}
                            {current.status === 'finished' && (
                                <div className={styles.doneBlock}>
                                    <span className={styles.doneCheck}>
                                        <Check size={30} strokeWidth={3} aria-hidden />
                                    </span>
                                    <div className={styles.doneAmount}>+{receivedBsv} BSV</div>
                                    <div className={styles.doneSub}>
                                        Added to your Treechat wallet.
                                    </div>
                                    {current.output_hash && (
                                        <a
                                            className={styles.doneLink}
                                            href={`https://whatsonchain.com/tx/${current.output_hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View on WhatsOnChain{' '}
                                            <ExternalLink size={11} aria-hidden />
                                        </a>
                                    )}
                                </div>
                            )}
                            {failedLike && (
                                <Banner
                                    kind="error"
                                    icon={<AlertTriangle size={18} />}
                                    title={
                                        current.status === 'refunded'
                                            ? 'Payment refunded'
                                            : current.status === 'failed'
                                            ? 'Swap failed'
                                            : current.status === 'creation_unknown'
                                            ? 'Swap not confirmed'
                                            : 'Swap expired'
                                    }
                                >
                                    If you already sent funds, contact{' '}
                                    <a href="mailto:support@changelly.com">support@changelly.com</a>{' '}
                                    before starting another swap.
                                </Banner>
                            )}
                            {current.error && <p className={styles.muted}>{current.error}</p>}
                            {(progress || awaiting) && (
                                <div className={styles.infoRow}>
                                    <Wallet size={18} className={styles.okIcon} aria-hidden />
                                    <div>
                                        <div className={styles.infoTitle}>
                                            You’ll receive ≈ {receivedBsv || '—'} BSV
                                        </div>
                                        <div className={styles.infoSub}>
                                            Lands in your wallet automatically when the swap
                                            completes.
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={styles.receivingSmall}>
                                Receiving BSV at <code>{current.payout_address}</code>
                            </div>
                            <div className={styles.footer}>
                                <div className={styles.footerMeta}>
                                    {current.external_id && (
                                        <span>
                                            Swap <code>{current.external_id}</code>
                                        </span>
                                    )}
                                    <a
                                        href="https://support.changelly.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Changelly support <ExternalLink size={10} aria-hidden />
                                    </a>
                                </div>
                                <div className={styles.footerActions}>
                                    {current.status === 'finished' ? (
                                        <button
                                            type="button"
                                            className={styles.doneButton}
                                            onClick={closeSheet}
                                        >
                                            Done
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.ghost}
                                            disabled={busy}
                                            onClick={refreshStatus}
                                        >
                                            <RefreshCw size={12} aria-hidden />
                                            <span>Refresh status</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        disabled={busy}
                                        onClick={startNewSwap}
                                    >
                                        {current.status === 'finished' ? 'Fund again' : 'New swap'}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.section}>
                                <div className={styles.labelRow}>
                                    <span className={styles.label}>You send</span>
                                    {from && (
                                        <span className={styles.helperMuted}>
                                            {coinNetwork(from)} network
                                        </span>
                                    )}
                                </div>
                                <div
                                    className={cn(
                                        styles.amountField,
                                        amountError && styles.amountFieldError,
                                    )}
                                >
                                    <input
                                        className={styles.amountInput}
                                        aria-label={`You send (${unitLabel})`}
                                        inputMode={
                                            unit === 'native' && from === 'btc'
                                                ? 'numeric'
                                                : 'decimal'
                                        }
                                        value={amount}
                                        placeholder="0"
                                        disabled={busy || creationAttempted}
                                        maxLength={unit === 'native' && from === 'btc' ? 18 : 19}
                                        aria-invalid={!!amountError}
                                        onChange={event => onAmountChange(event.target.value)}
                                    />
                                    <CurrencyPicker
                                        label="Send currency"
                                        disc={
                                            unit === 'usd' ? (
                                                <span className={styles.usdDisc} aria-hidden>
                                                    $
                                                </span>
                                            ) : (
                                                <CoinDisc from={from} size={22} />
                                            )
                                        }
                                        text={unitLabel}
                                        open={sendMenuOpen}
                                        onToggle={() => {
                                            setGetMenuOpen(false)
                                            setSendMenuOpen(value => !value)
                                        }}
                                        onClose={() => setSendMenuOpen(false)}
                                        options={[
                                            ...currencies.map(currency => ({
                                                key: currency.ticker,
                                                disc: <CoinDisc from={currency.ticker} size={24} />,
                                                title: coinTicker(currency.ticker),
                                                sub: `${coinNetwork(
                                                    currency.ticker,
                                                    currency.label,
                                                )} network`,
                                                selected: currency.ticker === from,
                                                disabled: busy || creationAttempted,
                                                onSelect: () => selectCoin(currency.ticker),
                                            })),
                                            {
                                                key: 'usd',
                                                divider: true,
                                                disc: (
                                                    <span className={styles.usdDisc} aria-hidden>
                                                        $
                                                    </span>
                                                ),
                                                title: 'Enter in USD',
                                                sub: usdAssist
                                                    ? `≈ ${formatUsd(perCoin || 0)} per ${
                                                          from === 'btc' ? 'BTC' : nativeUnit(from)
                                                      }`
                                                    : 'USD price unavailable',
                                                selected: unit === 'usd',
                                                disabled: !usdAssist || busy || creationAttempted,
                                                onSelect: toggleUsd,
                                            },
                                        ]}
                                    />
                                </div>
                                <div className={styles.helper}>
                                    {amountError ? (
                                        <span role="alert" className={styles.helperError}>
                                            <AlertTriangle size={13} aria-hidden />
                                            <span>{amountError}</span>
                                        </span>
                                    ) : (
                                        <span>{approxLine}</span>
                                    )}
                                </div>
                                {coinUnavailable ? (
                                    <p
                                        role="alert"
                                        className={cn(styles.error, styles.chipsFallback)}
                                    >
                                        <span>{coinUnavailable}</span>
                                        <button
                                            type="button"
                                            className={styles.inlineButton}
                                            onClick={() => setRefresh(value => value + 1)}
                                        >
                                            Try again
                                        </button>
                                    </p>
                                ) : activeLimits ? (
                                    <div className={styles.chips}>
                                        <button
                                            type="button"
                                            className={styles.chipButton}
                                            aria-label="Use minimum"
                                            disabled={busy || creationAttempted}
                                            onClick={fillMin}
                                        >
                                            Min {formatAmount(from, activeLimits.minimum)}
                                            {minUsd ? (
                                                <span className={styles.chipHint}>
                                                    {' '}
                                                    ≈ {formatUsdShort(minUsd)}
                                                </span>
                                            ) : null}
                                        </button>
                                        {usdAssist &&
                                            [25, 50, 100]
                                                .filter(
                                                    value =>
                                                        (!capUsd || value < capUsd) &&
                                                        value >= (minUsd || 0),
                                                )
                                                .map(value => (
                                                    <button
                                                        type="button"
                                                        key={value}
                                                        className={styles.chipButton}
                                                        disabled={busy || creationAttempted}
                                                        onClick={() => fillUsd(value)}
                                                    >
                                                        ${value}
                                                    </button>
                                                ))}
                                        <button
                                            type="button"
                                            className={styles.chipButton}
                                            aria-label="Use maximum"
                                            disabled={busy || creationAttempted}
                                            onClick={fillMax}
                                        >
                                            {capBinds ? `Max ${capLabel}` : 'Max'}
                                        </button>
                                    </div>
                                ) : limitsError ? (
                                    <p
                                        role="alert"
                                        className={cn(styles.error, styles.chipsFallback)}
                                    >
                                        <span>{limitsError}</span>
                                        <button
                                            type="button"
                                            className={styles.inlineButton}
                                            onClick={() => setRefresh(value => value + 1)}
                                        >
                                            Retry limits
                                        </button>
                                    </p>
                                ) : (
                                    <p className={cn(styles.muted, styles.chipsFallback)}>
                                        Loading swap limits…
                                    </p>
                                )}
                                {!currencies.length && (
                                    <p className={styles.muted}>
                                        No funding currencies are currently available. Reopen this
                                        panel to retry.
                                    </p>
                                )}
                            </div>

                            <div className={styles.direction} aria-hidden>
                                <span className={styles.directionLine} />
                                <span className={styles.directionDisc}>
                                    <ArrowDown size={14} />
                                </span>
                                <span className={styles.directionLine} />
                            </div>

                            <div className={styles.section} aria-live="polite">
                                <div className={styles.labelRow}>
                                    <span className={styles.label}>You get</span>
                                    <span className={styles.helperMuted}>
                                        to your Treechat wallet
                                    </span>
                                </div>
                                <div className={cn(styles.amountField, styles.amountFieldReadonly)}>
                                    <input
                                        className={styles.amountInput}
                                        aria-label="You get (BSV)"
                                        readOnly
                                        tabIndex={-1}
                                        value={showQuote ? quote?.expected_bsv ?? '' : ''}
                                        placeholder={quoting ? '…' : '0'}
                                    />
                                    <CurrencyPicker
                                        label="Receive currency"
                                        disc={
                                            <span className={styles.bsvDisc} aria-hidden>
                                                ₿
                                            </span>
                                        }
                                        text="BSV"
                                        open={getMenuOpen}
                                        onToggle={() => {
                                            setSendMenuOpen(false)
                                            setGetMenuOpen(value => !value)
                                        }}
                                        onClose={() => setGetMenuOpen(false)}
                                        options={[
                                            {
                                                key: 'bsv',
                                                disc: (
                                                    <span className={styles.bsvDisc} aria-hidden>
                                                        ₿
                                                    </span>
                                                ),
                                                title: 'BSV',
                                                sub: address
                                                    ? `Bitcoin SV · ${shortAddress(address)}`
                                                    : 'Bitcoin SV · address not ready yet',
                                                selected: true,
                                                onSelect: () => {},
                                            },
                                        ]}
                                    />
                                </div>
                                <div className={styles.helper}>
                                    <span>
                                        {showQuote
                                            ? expired
                                                ? 'Quote expired'
                                                : quoteUsd !== null
                                                ? `≈ ${formatUsd(quoteUsd)} USD`
                                                : 'USD estimate unavailable'
                                            : amountError
                                            ? 'out of range'
                                            : quoting
                                            ? 'Updating estimate…'
                                            : ''}
                                    </span>
                                </div>
                            </div>

                            {!address && (
                                <p className={styles.muted}>
                                    Your wallet’s BSV receiving address is not ready yet.
                                </p>
                            )}
                            <label className={styles.terms}>
                                <input
                                    type="checkbox"
                                    className={styles.termsBox}
                                    checked={accepted}
                                    disabled={busy}
                                    onChange={event => setAccepted(event.target.checked)}
                                />
                                <span>
                                    I accept Changelly’s{' '}
                                    <a
                                        href="https://changelly.com/terms-of-use"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Terms of Use
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://changelly.com/aml-kyc"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        AML/KYC Policy
                                    </a>{' '}
                                    and meet its eligibility requirements. Some swaps are held for
                                    identity verification.
                                </span>
                            </label>
                            <button
                                type="button"
                                className={styles.cta}
                                disabled={ctaDisabled}
                                onClick={createSwap}
                            >
                                {creationAttempted && error ? 'Retry' : 'Get deposit address'}
                            </button>
                            <p className={styles.note}>
                                Floating rate via Changelly — the final BSV amount can differ
                                slightly from the estimate.
                            </p>
                        </>
                    )}
                </Tray>
            )}
        </section>
    )
}

function quoteAmountSafe(from: string, value: string) {
    try {
        return quoteAmount(from, value)
    } catch (_) {
        return '0'
    }
}

function FeaturedStatus({ tx, now }: { tx: SwapTransaction; now: number }) {
    let tone = styles.dot_purple
    let word = ''
    let rest = ''
    switch (tx.status) {
        case 'creating':
        case 'new':
        case 'waiting': {
            tone = styles.dot_orange
            word = 'Awaiting payment'
            const remaining = paymentDeadline(tx) - now
            rest =
                remaining > 0
                    ? `${formatCountdown(remaining).replace(/:\d\d$/, '')} left`
                    : 'payment window ended'
            break
        }
        case 'confirming':
            word = 'Confirming'
            rest = `on the ${coinNetwork(tx.from_currency, tx.currency_label)} network`
            break
        case 'exchanging':
            word = 'Exchanging'
            rest = 'BSV on its way'
            break
        case 'sending':
            word = 'Sending BSV'
            rest = 'almost there'
            break
        case 'hold':
            tone = styles.dot_warn
            word = 'Needs verification'
            rest = 'tap for details'
            break
        case 'finished':
            tone = styles.dot_green
            word = 'Done'
            rest = `+${formatBsv(tx.actual_bsv || tx.expected_bsv) || ''} BSV added`
            break
        default:
            word = STATUS_LABELS[tx.status] || tx.status
    }
    return (
        <>
            <span className={cn(styles.dot, tone)} aria-hidden />
            <span className={cn(styles.statusWord, tone)}>{word}</span>
            {rest && <span className={styles.statusRest}> · {rest}</span>}
        </>
    )
}
